import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  IoArrowUndoOutline,
  IoCashOutline,
  IoPeopleOutline,
  IoTicketOutline,
  IoWalletOutline,
} from "react-icons/io5";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/firebase-config";
import { getStripeMode } from "../../lib/stripeMode";
import { summarizeEventMoney } from "../../lib/eventMoney";
import { HostInfoModal } from "./HostInfoModal";

const money = (cents) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format((Number(cents) || 0) / 100);

const when = (value) => {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

const Stat = ({ icon, label, value, hint }) => (
  <Box bg="gray.50" border="1px solid" borderColor="gray.100" borderRadius="xl" px={3} py={3}>
    <HStack spacing={2} mb={1}>
      <Icon as={icon} boxSize="14px" color="#508cf7" />
      <Text fontSize="11px" fontWeight="600" color="gray.500">
        {label}
      </Text>
    </HStack>
    <Text fontSize="lg" fontWeight="700" color="gray.800" lineHeight="1.1">
      {value}
    </Text>
    {hint && (
      <Text fontSize="10px" color="gray.400" mt={1}>
        {hint}
      </Text>
    )}
  </Box>
);

export const EventDashboardModal = ({ isOpen, onClose, event, onChanged }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [liveEvent, setLiveEvent] = useState(event);
  const [refundingId, setRefundingId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [profilePerson, setProfilePerson] = useState(null);
  const cancelRefundRef = useRef(null);

  const eventId = event?.docId || event?.id;

  useEffect(() => {
    if (!isOpen || !eventId) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [eventSnap, ticketSnap] = await Promise.all([
          getDoc(doc(db, "events", eventId)),
          getDocs(collection(db, "events", eventId, "tickets")),
        ]);
        const eventData = eventSnap.exists() ? { docId: eventSnap.id, ...eventSnap.data() } : event;
        const tickets = ticketSnap.docs.map((ticket) => ({ id: ticket.id, ...ticket.data() }));

        const memberIds = (eventData.members || [])
          .map((member) => (typeof member === "string" ? member : member?.id))
          .filter(Boolean);
        const ticketUserIds = tickets.map((ticket) => ticket.user).filter(Boolean);
        const userIds = [...new Set([...memberIds, ...ticketUserIds])];
        const userSnaps = await Promise.all(userIds.map((uid) => getDoc(doc(db, "users", uid))));
        const users = {};
        userSnaps.forEach((snap) => {
          if (snap.exists()) users[snap.id] = snap.data();
        });

        const byUser = new Map();
        tickets.forEach((ticket) => {
          if (!ticket.user || ticket.user === eventData.host) return;
          byUser.set(ticket.id, {
            key: ticket.id,
            ticketId: ticket.id,
            uid: ticket.user,
            user: users[ticket.user] || {},
            paidAmount: Math.max(0, Math.round(Number(ticket.paidAmount) || 0)),
            producerShare: Math.max(0, Math.round(Number(ticket.producerShare) || 0)),
            paymentIntentId: ticket.paymentIntentId || null,
            status: ticket.status === "refunded" ? "refunded" : "joined",
            createdAt: ticket.createdAt || null,
          });
        });

        memberIds.forEach((uid) => {
          if (uid === eventData.host) return;
          const alreadyListed = [...byUser.values()].some((row) => row.uid === uid && row.status !== "refunded");
          if (alreadyListed) return;
          byUser.set(`member-${uid}`, {
            key: `member-${uid}`,
            ticketId: null,
            uid,
            user: users[uid] || {},
            paidAmount: 0,
            producerShare: 0,
            paymentIntentId: null,
            status: "joined",
            createdAt: null,
          });
        });

        const list = [...byUser.values()].sort((a, b) => {
          if (a.status !== b.status) return a.status === "refunded" ? 1 : -1;
          return (b.paidAmount || 0) - (a.paidAmount || 0);
        });

        if (!cancelled) {
          setLiveEvent(eventData);
          setRows(list);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          toast({
            title: "Couldn't load this event",
            description: error.message,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventId, toast, event]);

  const active = rows.filter((row) => row.status !== "refunded");
  const paid = active.filter((row) => row.paidAmount > 0);
  const collected = active.reduce((sum, row) => sum + row.paidAmount, 0);
  const earned = active.reduce((sum, row) => sum + row.producerShare, 0);
  const moneySummary = summarizeEventMoney(liveEvent);
  const capacity = liveEvent?.maxMembers || 0;

  const refund = async (row) => {
    setRefundingId(row.ticketId);
    try {
      const refundEventTicket = httpsCallable(functions, "refundEventTicket");
      await refundEventTicket({
        eventId,
        ticketId: row.ticketId,
        mode: getStripeMode(),
      });
      setRows((current) =>
        current.map((item) => (item.ticketId === row.ticketId ? { ...item, status: "refunded" } : item))
      );
      setConfirmRow(null);
      toast({
        title: "Refund sent",
        description: `${row.user.displayName || "This guest"} will get ${money(row.paidAmount)} back.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      if (onChanged) onChanged();
    } catch (error) {
      toast({
        title: "Refund failed",
        description: error.message || "Stripe couldn't refund this ticket.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRefundingId(null);
    }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "3xl" }} scrollBehavior="inside" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
      <ModalContent borderRadius={{ base: 0, md: "2xl" }} overflow="hidden">
        <ModalHeader pb={2} pt={5} px={6} borderBottom="1px solid" borderColor="gray.100">
          <Text fontSize="lg" fontWeight="700" color="gray.800" noOfLines={1} pr={8}>
            {event?.name || "Event dashboard"}
          </Text>
          <Text fontSize="xs" color="gray.400" fontWeight="500">
            Guests, sales, and refunds
          </Text>
          <ModalCloseButton top={4} right={4} />
        </ModalHeader>
        <ModalBody px={{ base: 4, md: 6 }} py={5}>
          {loading ? (
            <Flex align="center" justify="center" minH="240px">
              <Spinner color="#508cf7" />
            </Flex>
          ) : (
            <VStack align="stretch" spacing={5}>
              <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={3}>
                <Stat
                  icon={IoPeopleOutline}
                  label="Guests"
                  value={String(active.length)}
                  hint={capacity > 0 ? `of ${capacity} spots` : "No cap set"}
                />
                <Stat icon={IoTicketOutline} label="Paid tickets" value={String(paid.length)} />
                <Stat icon={IoCashOutline} label="Collected" value={money(collected)} />
                <Stat
                  icon={IoWalletOutline}
                  label="Your earnings"
                  value={money(earned)}
                  hint={
                    moneySummary.venueMinimum > 0
                      ? `Venue ${money(moneySummary.venueCovered)} of ${money(moneySummary.venueMinimum)}`
                      : `Platform ${money(moneySummary.platformEarned)}`
                  }
                />
              </Grid>

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.800" mb={2}>
                  Who joined
                </Text>
                {rows.length === 0 ? (
                  <Box bg="gray.50" borderRadius="xl" px={4} py={8} textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                      No guests yet. People who buy a ticket or join free will show up here.
                    </Text>
                  </Box>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {rows.map((row) => {
                      const name = row.user.displayName || "Guest";
                      const refunded = row.status === "refunded";
                      const canRefund = !refunded && row.paidAmount > 0 && row.paymentIntentId && row.ticketId;
                      return (
                        <Flex
                          key={row.key}
                          align="center"
                          gap={3}
                          px={3}
                          py={3}
                          border="1px solid"
                          borderColor="gray.100"
                          borderRadius="xl"
                          bg={refunded ? "gray.50" : "white"}
                        >
                          <HStack
                            flex="1"
                            minW={0}
                            spacing={3}
                            cursor="pointer"
                            role="button"
                            onClick={() => setProfilePerson({ ...row.user, displayName: name })}
                            _hover={{ opacity: 0.8 }}
                          >
                            <Avatar size="sm" name={name} src={row.user.avatarUrl || row.user.photoURL} />
                            <Box minW={0}>
                              <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                                {name}
                              </Text>
                              <Text fontSize="xs" color="gray.400" noOfLines={1}>
                                {row.user.email || "No email"}
                                {row.createdAt ? ` · ${when(row.createdAt)}` : ""}
                              </Text>
                            </Box>
                          </HStack>
                          <HStack spacing={3} flexShrink={0}>
                            <Box textAlign="right">
                              <Text
                                fontSize="sm"
                                fontWeight="700"
                                color={refunded ? "gray.400" : row.paidAmount > 0 ? "gray.800" : "green.600"}
                                textDecoration={refunded ? "line-through" : "none"}
                              >
                                {row.paidAmount > 0 ? money(row.paidAmount) : "Free"}
                              </Text>
                              {refunded && (
                                <Text fontSize="10px" fontWeight="700" color="gray.400" letterSpacing="0.04em">
                                  REFUNDED
                                </Text>
                              )}
                            </Box>
                            {canRefund && (
                              <Button
                                size="sm"
                                variant="outline"
                                borderRadius="full"
                                h="32px"
                                px={3}
                                fontWeight="600"
                                fontSize="12px"
                                color="red.500"
                                borderColor="red.200"
                                bg="white"
                                leftIcon={<Icon as={IoArrowUndoOutline} boxSize="14px" />}
                                _hover={{ bg: "red.50", borderColor: "red.300" }}
                                onClick={() => setConfirmRow(row)}
                              >
                                Refund
                              </Button>
                            )}
                          </HStack>
                        </Flex>
                      );
                    })}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>

      <AlertDialog
        isOpen={!!confirmRow}
        leastDestructiveRef={cancelRefundRef}
        onClose={() => {
          if (!refundingId) setConfirmRow(null);
        }}
        isCentered
      >
        <AlertDialogOverlay backdropFilter="blur(4px)" bg="blackAlpha.600">
          <AlertDialogContent borderRadius="2xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="700" color="gray.800" pb={1}>
              Refund {confirmRow ? money(confirmRow.paidAmount) : ""}?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                {confirmRow?.user.displayName || "This guest"} gets the full ticket price back on their card.
                They come off the guest list. This can't be undone.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRefundRef}
                variant="ghost"
                fontWeight="600"
                borderRadius="lg"
                onClick={() => setConfirmRow(null)}
                isDisabled={!!refundingId}
              >
                Keep ticket
              </Button>
              <Button
                bg="red.500"
                color="white"
                fontWeight="600"
                borderRadius="lg"
                ml={3}
                _hover={{ bg: "red.600" }}
                isLoading={refundingId === confirmRow?.ticketId}
                onClick={() => confirmRow && refund(confirmRow)}
              >
                Refund {confirmRow ? money(confirmRow.paidAmount) : ""}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <HostInfoModal
        isOpen={!!profilePerson}
        onClose={() => setProfilePerson(null)}
        host={profilePerson}
        showHostedBy={false}
      />
    </>
  );
};
